import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Col, Row, Table, Typography } from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import pmvService from '@/services/pmv.service';

const { Text } = Typography;

const PmvReliabilityForecastPage: React.FC = () => {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['pmv-reliability-forecast'],
    queryFn: () => pmvService.getReliabilityForecast(),
  });
  const planned = Array.isArray(q.data?.planned)
    ? q.data?.planned
    : q.data?.planned?.items || [];
  const predicted = Array.isArray(q.data?.predicted)
    ? q.data?.predicted
    : q.data?.predicted?.items || [];

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Maintenance Forecast"
        subtitle="Planned PM and predicted interventions shown separately"
        primaryActions={
          <Button onClick={() => navigate('/asset/pmv/reliability/control-tower')}>
            Control Tower
          </Button>
        }
      />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={q.data?.note || 'PLANNED ≠ PREDICTED — labels mandatory'}
      />
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <ControlTowerSectionCard zone="metricsOrders" title="PLANNED — Maintenance schedule">
            <Table
              size="small"
              rowKey={(r) => String(r.id || r.title)}
              loading={q.isLoading}
              dataSource={planned as Array<Record<string, unknown>>}
              pagination={{ pageSize: 10 }}
              columns={[
                { title: 'Title', dataIndex: 'title' },
                {
                  title: 'Next due',
                  dataIndex: 'next_due_date',
                  render: (v) => (v ? String(v) : '—'),
                },
              ]}
              locale={{ emptyText: 'No planned PM rows visible' }}
            />
          </ControlTowerSectionCard>
        </Col>
        <Col xs={24} lg={12}>
          <ControlTowerSectionCard zone="attention" title="PREDICTED — Reliability advisories">
            <Table
              size="small"
              rowKey={(r) => String(r.id || r.title)}
              loading={q.isLoading}
              dataSource={predicted as Array<Record<string, unknown>>}
              pagination={{ pageSize: 10 }}
              columns={[
                { title: 'Title', dataIndex: 'title' },
                {
                  title: 'Window',
                  render: (_, r) =>
                    r.window_low != null
                      ? `${r.window_low}–${r.window_high} ${r.window_unit || ''}`
                      : '—',
                },
                { title: 'Confidence', dataIndex: 'confidence' },
              ]}
              locale={{ emptyText: 'No active predictions' }}
            />
            <Text type="secondary">
              Forecast based on available operational evidence.
            </Text>
          </ControlTowerSectionCard>
        </Col>
      </Row>
    </WorkspaceLayout>
  );
};

export default PmvReliabilityForecastPage;
